/**
 * ConnectionManager — Manages multiple PeerConnections for a single robot.
 *
 * Provides centralized lifecycle: connect/disconnect/reconnect all or individual peers.
 * Shares local media via SharedMediaManager.
 * Routes signaling events (SDP answer, ICE candidate, robot online/offline)
 * from the EventBus to the right PeerConnection.
 * Coordinates reconnection across all peers.
 *
 * No React dependencies — pure JS class.
 */

import { PeerConnection } from "./PeerConnection";
import { SharedMediaManager } from "./SharedMediaManager";
import { PEER_STATES, PEER_INTERNAL_EVENTS, RECONNECT_DEFAULTS } from "./peerStates";
import { ROBOT_EVENTS, getRobotEvent } from "shared/services/eventBus/eventTypes";
import { peerHasCamera } from "features/robot-teleop/utils/peerMedia";

export class ConnectionManager {
  /**
   * @param {string} robotId
   * @param {Object} config
   * @param {Object} config.eventBus - EventBus instance
   * @param {Object} config.signalingBridge - { sendOffer, sendIceCandidate, sendMediaConfig, sendEvent }
   * @param {Object} [config.iceConfig]
   * @param {Object} [config.dataChannelsConfig]
   * @param {boolean} [config.trickleICE=true]
   */
  constructor(robotId, config) {
    this.robotId = robotId;
    this.eventBus = config.eventBus;
    this.signalingBridge = config.signalingBridge;
    this.iceConfig = config.iceConfig;
    this.dataChannelsConfig = config.dataChannelsConfig;
    this.trickleICE = config.trickleICE ?? true;

    /** @type {Map<string, PeerConnection>} */
    this.peers = new Map();
    this.controlPeerId = null;
    this.sharedMedia = new SharedMediaManager();
    this._destroyed = false;
    /** @type {Map<string, Function[]>} peerId → unsubscribe functions */
    this._peerSubscriptions = new Map();
  }

  get isDestroyed() { return this._destroyed; }

  // ==================== Initialization ====================

  /**
   * Create (or update) one PeerConnection per peer of the robot.
   * Idempotent: peers whose id is already known are kept as-is (their peerInfo
   * and autoConnect flag are refreshed), peers that disappeared are destroyed,
   * new ones are created. Calling it twice with the same peers therefore never
   * tears down a live connection.
   *
   * Does NOT connect — call connectAll() or connectAutoConnectPeers() after.
   *
   * @param {Array} peerInfos - the robot's peers as registered with conn-manager
   *   (its "robot clients", GET /api/user/robots/<robotId>)
   * @param {Object} [connectConfig] - Override autoConnect per peer: { [peerId]: { autoConnect: boolean } }
   */
  initialize(peerInfos, connectConfig = {}) {
    if (this._destroyed) return;

    const infos = (peerInfos || []).filter((info) => info && info.id);
    const nextIds = new Set(infos.map((info) => info.id));

    // Drop peers that no longer exist
    for (const [peerId, peer] of Array.from(this.peers.entries())) {
      if (!nextIds.has(peerId)) {
        this._unsubscribePeer(peerId);
        peer.destroy();
        this.peers.delete(peerId);
      }
    }

    // Determine control peer
    const controlInfo = infos.find(
      (info) => info.setting?.is_control_channel === true
    ) || infos[0];
    this.controlPeerId = controlInfo?.id || null;

    // Create or refresh a PeerConnection for each peer
    for (const info of infos) {
      const peerId = info.id;
      const autoConnect = this._resolveAutoConnect(info, connectConfig);

      const existing = this.peers.get(peerId);
      if (existing) {
        existing.peerInfo = info;
        existing.autoConnect = autoConnect;
        continue;
      }

      const peer = new PeerConnection(peerId, {
        peerInfo: info,
        getMicrophoneTrack: () => this.sharedMedia.getMicrophoneTrack(),
        signalingBridge: this.signalingBridge,
        eventBus: this.eventBus,
        iceConfig: this.iceConfig,
        dataChannelsConfig: this.dataChannelsConfig,
        autoConnect,
        trickleICE: this.trickleICE,
      });

      this.peers.set(peerId, peer);
      this._subscribePeer(peerId);
    }

    console.log(
      `[ConnectionManager:${this.robotId}] Initialized ${this.peers.size} peers, control: ${this.controlPeerId}`
    );
  }

  _resolveAutoConnect(peerInfo, connectConfig) {
    // Explicit override > default logic (autoConnect if control channel or has cameras)
    const override = connectConfig?.[peerInfo.id]?.autoConnect;
    if (override !== undefined) return !!override;
    return !!peerInfo.setting?.is_control_channel || peerHasCamera(peerInfo);
  }

  // ==================== Connection Lifecycle ====================

  /**
   * Connect ALL peers (regardless of autoConnect flag).
   */
  async connectAll() {
    const stream = await this._getLocalStream();
    if (this._destroyed) return;
    const promises = [];
    for (const peer of this.peers.values()) {
      peer.userClosed = false;
      promises.push(peer.connect(stream));
    }
    await Promise.allSettled(promises);
  }

  /**
   * Connect only peers with autoConnect=true (and not disconnected by the user).
   */
  async connectAutoConnectPeers() {
    const stream = await this._getLocalStream();
    if (this._destroyed) return;
    const promises = [];
    for (const peer of this.peers.values()) {
      if (peer.autoConnect && !peer.userClosed) {
        promises.push(peer.connect(stream));
      }
    }
    await Promise.allSettled(promises);
  }

  /**
   * Connect a specific peer by ID (for manual user-triggered connections).
   * @param {string} peerId
   */
  async connectPeer(peerId) {
    const peer = this.peers.get(peerId);
    if (!peer) {
      console.warn(`[ConnectionManager:${this.robotId}] Unknown peer: ${peerId}`);
      return;
    }
    peer.userClosed = false;
    const stream = await this._getLocalStream();
    if (this._destroyed) return;
    await peer.connect(stream);
  }

  /**
   * Disconnect a specific peer (user action). It stays disconnected — the
   * automatic paths (auto-connect, robot online, signaling reconnect, retry)
   * skip it — until connectPeer()/connectAll() is called for it again.
   * @param {string} peerId
   */
  disconnectPeer(peerId) {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    peer.userClosed = true;
    peer.close();
  }

  /**
   * Disconnect all peers.
   */
  disconnectAll() {
    for (const peer of this.peers.values()) {
      peer.close();
    }
  }

  /**
   * Reconnect only peers that are in FAILED or CLOSED state and were previously connected.
   */
  async reconnectFailed() {
    const stream = await this._getLocalStream();
    const peers = this.getAllPeers().filter(
      (p) => (p.state === PEER_STATES.FAILED || p.state === PEER_STATES.CLOSED) && p.wasConnected && !p.userClosed
    );
    await this._reconnectStaggered(peers, stream);
  }

  /**
   * Reconnect every peer that should be connected (previously connected or
   * autoConnect) but currently is not. Used after the signaling server
   * reconnects. Peers whose WebRTC link is still healthy are left alone —
   * a signaling outage does not break an established P2P connection.
   * Staggers connections to avoid thundering herd.
   */
  async reconnectAll() {
    const stream = await this._getLocalStream();
    const peers = this.getAllPeers().filter(
      (p) => (p.wasConnected || p.autoConnect) && p.state !== PEER_STATES.CONNECTED && !p.userClosed
    );
    await this._reconnectStaggered(peers, stream);
  }

  async _reconnectStaggered(peers, stream) {
    for (let i = 0; i < peers.length; i++) {
      if (i > 0) await this._delay(RECONNECT_DEFAULTS.staggerDelay);
      if (this._destroyed) return;
      peers[i].reconnect(stream);
    }
  }

  /**
   * Full teardown — close all connections, release media, unsubscribe events.
   */
  destroy() {
    if (this._destroyed) return;
    this._destroyed = true;
    for (const peerId of Array.from(this._peerSubscriptions.keys())) {
      this._unsubscribePeer(peerId);
    }
    for (const peer of this.peers.values()) {
      peer.destroy();
    }
    this.peers.clear();
    this.sharedMedia.destroy();
  }

  // ==================== Peer Access ====================

  /**
   * Get a specific PeerConnection.
   * @param {string} peerId
   * @returns {PeerConnection|undefined}
   */
  getPeer(peerId) {
    return this.peers.get(peerId);
  }

  /**
   * Get the control peer connection.
   * @returns {PeerConnection|undefined}
   */
  getControlPeer() {
    return this.peers.get(this.controlPeerId);
  }

  /**
   * Get all peer connections as array.
   * @returns {PeerConnection[]}
   */
  getAllPeers() {
    return Array.from(this.peers.values());
  }

  /**
   * Get only auto-connect peers.
   * @returns {PeerConnection[]}
   */
  getAutoConnectPeers() {
    return this.getAllPeers().filter((p) => p.autoConnect);
  }

  /**
   * Get only manual-connect peers.
   * @returns {PeerConnection[]}
   */
  getManualConnectPeers() {
    return this.getAllPeers().filter((p) => !p.autoConnect);
  }

  /**
   * Overall state: 'connected' if all are connected, 'partial' if some, 'disconnected' if none.
   * @returns {'connected'|'partial'|'disconnected'}
   */
  getOverallState() {
    const peers = this.getAllPeers();
    if (peers.length === 0) return 'disconnected';

    const connected = peers.filter((p) => p.state === PEER_STATES.CONNECTED);
    if (connected.length === peers.length) return 'connected';
    if (connected.length > 0) return 'partial';
    return 'disconnected';
  }

  // ==================== Per-Peer Config ====================

  /**
   * Change autoConnect setting for a specific peer at runtime.
   * @param {string} peerId
   * @param {boolean} enabled
   */
  setAutoConnect(peerId, enabled) {
    const peer = this.peers.get(peerId);
    if (peer) peer.autoConnect = enabled;
  }

  /**
   * Send (or stop sending) the browser microphone to one peer. Enabling it
   * opens the microphone on first use (getUserMedia prompt) and rejects when
   * it is denied; the microphone is released once no peer uses it anymore.
   * @param {string} peerId
   * @param {boolean} enabled
   */
  async setMicrophoneEnabled(peerId, enabled) {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    if (enabled) {
      const track = await this.sharedMedia.acquireMicrophone();
      if (this._destroyed) return;
      peer.setMicrophoneEnabled(true, track);
      return;
    }
    peer.setMicrophoneEnabled(false);
    if (!this.getAllPeers().some((p) => p.microphoneEnabled)) {
      this.sharedMedia.releaseMicrophone();
    }
  }

  /**
   * Receive (or mute) one peer's microphone; see PeerConnection.setRemoteAudioEnabled.
   * @param {string} peerId
   * @param {boolean} enabled
   */
  setRemoteAudioEnabled(peerId, enabled) {
    const peer = this.peers.get(peerId);
    if (peer) peer.setRemoteAudioEnabled(enabled);
  }

  // ==================== Media ====================

  /**
   * Acquire local media for sharing across peer connections.
   * @param {MediaStreamConstraints} constraints
   * @returns {Promise<MediaStream|null>}
   */
  async acquireLocalMedia(constraints) {
    return this.sharedMedia.acquire(constraints);
  }

  /**
   * Release shared local media.
   */
  releaseLocalMedia() {
    this.sharedMedia.release();
  }

  /**
   * Current shared stream, acquiring the default (synthetic) one if needed.
   * Never throws — a media problem must not prevent control/data connections.
   * @returns {Promise<MediaStream|null>}
   */
  async _getLocalStream() {
    const existing = this.sharedMedia.getStream();
    if (existing) return existing;
    try {
      return await this.sharedMedia.acquire();
    } catch (err) {
      console.warn(`[ConnectionManager:${this.robotId}] Local media unavailable:`, err);
      return null;
    }
  }

  // ==================== Signaling Routing ====================

  /**
   * Route SDP answer to the correct PeerConnection.
   * @param {string} peerId
   * @param {Object} sdp - { type, sdp }
   */
  handlePeerSDP(peerId, sdp) {
    const peer = this.peers.get(peerId);
    if (peer) {
      peer.handleRemoteSDP(sdp);
    } else {
      console.warn(`[ConnectionManager:${this.robotId}] SDP for unknown peer: ${peerId}`);
    }
  }

  /**
   * Route ICE candidate to the correct PeerConnection.
   * @param {string} peerId
   * @param {Object} candidate
   */
  handlePeerIceCandidate(peerId, candidate) {
    const peer = this.peers.get(peerId);
    if (peer) {
      peer.handleRemoteIceCandidate(candidate);
    } else {
      console.warn(`[ConnectionManager:${this.robotId}] ICE candidate for unknown peer: ${peerId}`);
    }
  }

  /**
   * Handle a peer coming online.
   * Reconnects if autoConnect=true or peer was previously connected, unless the
   * existing WebRTC link is still healthy (a robot that merely re-registered
   * with the signaling server must not lose a working P2P connection).
   * @param {string} peerId
   */
  async handlePeerOnline(peerId) {
    const peer = this.peers.get(peerId);
    if (!peer) return;

    console.log(`[ConnectionManager:${this.robotId}] Peer ${peerId} online`);

    if (peer.state === PEER_STATES.CONNECTED) return;
    if (!peer.userClosed && (peer.autoConnect || peer.wasConnected)) {
      const stream = await this._getLocalStream();
      if (this._destroyed) return;
      peer.reconnect(stream);
    }
  }

  /**
   * Handle a peer going offline.
   * @param {string} peerId
   */
  handlePeerOffline(peerId) {
    const peer = this.peers.get(peerId);
    if (!peer) return;

    console.log(`[ConnectionManager:${this.robotId}] Peer ${peerId} offline`);
    // Don't close — the peer will fail naturally, retry with backoff and end
    // up FAILED; handlePeerOnline() brings it back when the robot returns.
  }

  // ==================== Internal ====================

  _subscribePeer(peerId) {
    this._unsubscribePeer(peerId);
    const bus = this.eventBus;
    const subs = [
      bus.subscribe(
        getRobotEvent(peerId, PEER_INTERNAL_EVENTS.RECONNECT_NEEDED),
        () => this._handlePeerReconnectNeeded(peerId)
      ),
      bus.subscribe(
        getRobotEvent(peerId, ROBOT_EVENTS.SIGNAL_SDP),
        (sdp) => this.handlePeerSDP(peerId, sdp)
      ),
      bus.subscribe(
        getRobotEvent(peerId, ROBOT_EVENTS.SIGNAL_CANDIDATE),
        (data) => {
          if (data?.IceCandidate) this.handlePeerIceCandidate(peerId, data.IceCandidate);
        }
      ),
      bus.subscribe(
        getRobotEvent(peerId, ROBOT_EVENTS.CONNECTED),
        () => this.handlePeerOnline(peerId)
      ),
      bus.subscribe(
        getRobotEvent(peerId, ROBOT_EVENTS.DISCONNECTED),
        () => this.handlePeerOffline(peerId)
      ),
    ];
    this._peerSubscriptions.set(peerId, subs);
  }

  _unsubscribePeer(peerId) {
    const subs = this._peerSubscriptions.get(peerId);
    if (!subs) return;
    subs.forEach((unsub) => {
      if (typeof unsub === 'function') unsub();
    });
    this._peerSubscriptions.delete(peerId);
  }

  async _handlePeerReconnectNeeded(peerId) {
    if (this._destroyed) return;
    const peer = this.peers.get(peerId);
    if (!peer || peer.userClosed) return;

    const stream = await this._getLocalStream();
    if (this._destroyed) return;
    peer.connect(stream);
  }

  _delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
